import { memo, useCallback, useEffect, useRef, useState } from 'react';
import './ElifHakan.css';
import { elifHakanInvitation as defaultData } from '../../data/invitations/elifHakanInvitation';

/* flap 0.9s + kart 1.15s + zoom 1.55s */
const ENVELOPE_OPEN_MS = 2300;
const CARD_ZOOM_MS = 1600;
const TOTAL_TRANSITION_MS = ENVELOPE_OPEN_MS + CARD_ZOOM_MS;

export default function ElifHakanInvitation({ data = defaultData, siteName = 'Eskişehir Davetiye' }) {
  const [phase, setPhase] = useState('landing');
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [musicError, setMusicError] = useState(false);
  const audioRef = useRef(null);
  const detailsRef = useRef(null);

  const { couple, event, envelope } = data;
  const isLanding = phase === 'landing';
  const isOpening = phase === 'opening';
  const isZooming = phase === 'zooming';
  const isRevealed = phase === 'revealed';
  const showMain = isZooming || isRevealed;

  const mobileStripPhotos = buildMobileStrip(data.gallery.mobile ?? data.gallery.left);

  const startMusic = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return false;
    try {
      audio.volume = 0.65;
      if (audio.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        audio.load();
      }
      await audio.play();
      setMusicPlaying(true);
      setMusicError(false);
      return true;
    } catch {
      setMusicError(true);
      setMusicPlaying(false);
      return false;
    }
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) audio.load();
  }, []);

  const openEnvelope = useCallback(() => {
    if (phase !== 'landing') return;

    const audio = audioRef.current;
    if (audio) {
      audio.volume = 0.65;
      const playAttempt = audio.play();
      if (playAttempt !== undefined) {
        playAttempt
          .then(() => {
            setMusicPlaying(true);
            setMusicError(false);
          })
          .catch(() => {
            setMusicError(true);
            setMusicPlaying(false);
          });
      }
    }

    setPhase('opening');
  }, [phase]);

  useEffect(() => {
    if (phase !== 'opening') return undefined;
    const timer = window.setTimeout(() => setPhase('zooming'), ENVELOPE_OPEN_MS);
    return () => window.clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'zooming') return undefined;
    const timer = window.setTimeout(() => setPhase('revealed'), CARD_ZOOM_MS);
    return () => window.clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    document.body.classList.add('elifhakan-active');
    return () => {
      document.body.classList.remove('elifhakan-active');
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (isRevealed) {
      document.body.style.overflow = '';
      return;
    }
    document.body.style.overflow = 'hidden';
  }, [isRevealed]);

  const scrollToDetails = () => {
    if (!isRevealed) {
      openEnvelope();
      window.setTimeout(() => {
        detailsRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, TOTAL_TRANSITION_MS + 200);
      return;
    }
    detailsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleMusic = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (musicPlaying) {
      audio.pause();
      setMusicPlaying(false);
      return;
    }
    await startMusic();
  };

  return (
    <div className="elifhakan">
      <div
        className={`elifhakan-particles${isRevealed ? ' elifhakan-particles--off' : ''}`}
        aria-hidden
      >
        {Array.from({ length: 10 }, (_, i) => (
          <span key={i} className="elifhakan-particles__dot" style={{ '--i': i }} />
        ))}
      </div>

      <audio
        ref={audioRef}
        src={data.music.src}
        loop
        preload="auto"
        playsInline
      />

      <section
        className={`elifhakan-landing${isOpening ? ' elifhakan-landing--opening' : ''}${isZooming ? ' elifhakan-landing--zoom' : ''}${isRevealed ? ' elifhakan-landing--exit' : ''}`}
        style={{ '--elif-bg': `url(${data.landing.background})` }}
        aria-hidden={isRevealed}
      >
        <div className="elifhakan-landing__content">
          <h1 className="elifhakan-landing__title">{data.landing.title}</h1>

          <div
            className={`elifhakan-envelope-wrap${isOpening || isZooming ? ' elifhakan-envelope-wrap--opening' : ''}${isZooming ? ' elifhakan-envelope-wrap--zoom' : ''}`}
          >
            <div className="elifhakan-envelope" aria-hidden={!isLanding && !isOpening && !isZooming}>
              <div className="elifhakan-envelope__card">
                <div className="elifhakan-envelope__card-paper" aria-hidden />
                <div className="elifhakan-envelope__card-border" aria-hidden />
                <div className="elifhakan-envelope__card-body">
                  <p className="elifhakan-envelope__card-eyebrow">Evleniyoruz</p>
                  <span className="elifhakan-envelope__card-initials">{couple.initials}</span>
                  <div className="elifhakan-envelope__card-divider" aria-hidden />
                  <p className="elifhakan-envelope__card-names">
                    {couple.bride}
                    <span className="elifhakan-envelope__card-amp">&amp;</span>
                    {couple.groom}
                  </p>
                  <p className="elifhakan-envelope__card-date">{event.date}</p>
                </div>
              </div>

              <div className="elifhakan-envelope__back" />

              <div className="elifhakan-envelope__inside">
                <div className="elifhakan-envelope__fold elifhakan-envelope__fold--left" />
                <div className="elifhakan-envelope__fold elifhakan-envelope__fold--right" />
              </div>

              <div className="elifhakan-envelope__pocket" />
              <div className="elifhakan-envelope__front-closed" />

              <div className="elifhakan-envelope__flap">
                <div className="elifhakan-envelope__flap-inner" />
              </div>

              <button
                type="button"
                className="elifhakan-envelope__hit"
                onClick={openEnvelope}
                disabled={!isLanding}
                aria-label="Zarfı aç"
              >
                <span className="elifhakan-envelope__seal">
                  <img
                    className="elifhakan-envelope__seal-img"
                    src={envelope.seal}
                    alt=""
                    draggable={false}
                  />
                  <span className="elifhakan-envelope__seal-monogram">{couple.initials}</span>
                </span>
              </button>
            </div>

            <div className="elifhakan-envelope__ground" aria-hidden />
          </div>

          <p className="elifhakan-landing__hint">{data.landing.hint}</p>
        </div>
      </section>

      <div className={`elifhakan-main${showMain ? ' elifhakan-main--visible' : ''}`}>
        <section
          className="elifhakan-hero"
          style={{ '--elif-bg': `url(${data.main.background})` }}
        >
          {showMain && (
            <>
              <PhotoMarquee side="left" photos={data.gallery.left} />
              <PhotoMarquee side="right" photos={data.gallery.right} />
            </>
          )}

          <div className="elifhakan-hero__stage">
            {showMain && (
              <PhotoBand position="top" photos={mobileStripPhotos} />
            )}

            <div className="elifhakan-hero__content">
              <p className="elifhakan-hero__eyebrow">{data.main.headline}</p>
            <p className="elifhakan-hero__tagline">{data.main.tagline}</p>
            <div className="elifhakan-hero__rule" aria-hidden />
            <h2 className="elifhakan-hero__names">
              <span className="elifhakan-hero__name">{couple.bride}</span>
              <span className="elifhakan-hero__amp" aria-hidden>&amp;</span>
              <span className="elifhakan-hero__name">{couple.groom}</span>
            </h2>
            <p className="elifhakan-hero__date">{event.date}</p>
            <p className="elifhakan-hero__message">{data.main.message}</p>

            <HeroCountdown dateISO={event.dateISO} />

            <button type="button" className="elifhakan-hero__cta" onClick={scrollToDetails}>
              <CalendarIcon />
              Etkinlik Detayları
            </button>
            <div className="elifhakan-scroll-hint" aria-hidden>
              <span className="elifhakan-scroll-hint__wheel" />
            </div>
          </div>

            {showMain && (
              <PhotoBand position="bottom" photos={mobileStripPhotos} reverse />
            )}
          </div>
        </section>

        {data.main.story && (
          <section className="elifhakan-story" aria-labelledby="elifhakan-story-title">
            <div className="elifhakan-story__inner">
              <p className="elifhakan-story__eyebrow">{data.main.story.title}</p>
              <h3 id="elifhakan-story-title" className="elifhakan-story__quote">
                &ldquo;{data.main.story.quote}&rdquo;
              </h3>
              <div className="elifhakan-story__timeline">
                {data.main.story.milestones.map(({ year, label }, i) => (
                  <div key={year} className="elifhakan-story__milestone">
                    <span className="elifhakan-story__year">{year}</span>
                    <span className="elifhakan-story__dot" aria-hidden />
                    <span className="elifhakan-story__label">{label}</span>
                    {i < data.main.story.milestones.length - 1 && (
                      <span className="elifhakan-story__connector" aria-hidden />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section ref={detailsRef} className="elifhakan-details" id="detaylar">
          <div className="elifhakan-details__inner">
            <p className="elifhakan-details__eyebrow">Save the Date</p>
            <h3 className="elifhakan-section-title">Etkinlik Detayları</h3>
            <div className="elifhakan-details__grid">
              <article className="elifhakan-detail-card">
                <CalendarDetailIcon />
                <h4>Tarih</h4>
                <p>{event.date}</p>
              </article>
              <article className="elifhakan-detail-card">
                <ClockIcon />
                <h4>Saat</h4>
                <p>{event.time}</p>
              </article>
              <article className="elifhakan-detail-card elifhakan-detail-card--wide">
                <PinIcon />
                <h4>Mekan</h4>
                <p>{event.venue}</p>
                <span className="elifhakan-detail-card__sub">{event.address}</span>
              </article>
            </div>
            <a
              className="elifhakan-details__map"
              href={event.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <PinIcon />
              Haritada Gör
            </a>
          </div>
        </section>

        <footer className="elifhakan-footer">
          <p>
            {couple.bride} &amp; {couple.groom}
          </p>
          <p className="elifhakan-footer__credit">{siteName}</p>
        </footer>
      </div>

      {isRevealed && (
        <button
          type="button"
          className="elifhakan-music-toggle"
          onClick={toggleMusic}
          aria-label={musicPlaying ? 'Müziği durdur' : 'Müziği başlat'}
          title={
            musicError
              ? 'Müzik dosyası bulunamadı — thinking_out_loud.mp3 ekleyin'
              : data.music.title
          }
        >
          {musicPlaying ? <PauseIcon /> : <PlayIcon />}
        </button>
      )}
    </div>
  );
}

function useCountdown(isoDate) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  if (!isoDate) return null;

  const target = new Date(isoDate).getTime();
  const diff = target - now;

  if (Number.isNaN(target)) return null;
  if (diff <= 0) return { past: true };

  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);

  return { past: false, days, hours, minutes, seconds };
}

function HeroCountdown({ dateISO }) {
  const countdown = useCountdown(dateISO);
  if (!countdown || countdown.past) return null;

  return (
    <div className="elifhakan-countdown" aria-label="Etkinliğe kalan süre">
      {[
        { value: countdown.days, label: 'Gün' },
        { value: countdown.hours, label: 'Saat' },
        { value: countdown.minutes, label: 'Dk' },
        { value: countdown.seconds, label: 'Sn' },
      ].map(({ value, label }) => (
        <div key={label} className="elifhakan-countdown__unit">
          <span className="elifhakan-countdown__value">{String(value).padStart(2, '0')}</span>
          <span className="elifhakan-countdown__label">{label}</span>
        </div>
      ))}
    </div>
  );
}

function buildMobileStrip(photos) {
  const unique = [];
  const seen = new Set();
  for (const photo of photos) {
    if (seen.has(photo.src)) continue;
    seen.add(photo.src);
    unique.push(photo);
  }
  return [...unique, ...unique, ...unique];
}

const PhotoBand = memo(function PhotoBand({ position, photos, reverse = false }) {
  const loop = [...photos, ...photos];

  return (
    <div className={`elifhakan-photo-band elifhakan-photo-band--${position}`} aria-hidden>
      <div
        className={`elifhakan-photo-band__track${
          reverse ? ' elifhakan-photo-band__track--reverse' : ''
        }`}
      >
        {loop.map((photo, i) => (
          <figure key={`${photo.src}-${i}`} className="elifhakan-photo-band__frame">
            <img
              src={photo.src}
              alt=""
              loading="lazy"
              decoding="async"
              draggable={false}
            />
          </figure>
        ))}
      </div>
    </div>
  );
});

const PhotoMarquee = memo(function PhotoMarquee({ side, photos }) {
  const doubled = [...photos.slice(0, 2), ...photos.slice(0, 2)];

  return (
    <div
      className={`elifhakan-photo-stream elifhakan-photo-stream--${side}`}
      aria-hidden
    >
      <div className="elifhakan-photo-stream__fade elifhakan-photo-stream__fade--top" />
      <div className="elifhakan-photo-stream__fade elifhakan-photo-stream__fade--bottom" />
      <div className={`elifhakan-photo-stream__track elifhakan-photo-stream__track--${side}`}>
        {doubled.map((photo, i) => (
          <figure key={`${photo.src}-${i}`} className="elifhakan-photo-stream__frame">
            <img
              src={photo.src}
              alt=""
              loading="lazy"
              decoding="async"
              draggable={false}
            />
          </figure>
        ))}
      </div>
    </div>
  );
});

function CalendarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 3v4M16 3v4M3 10h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M6 5h4v14H6V5zm8 0h4v14h-4V5z" />
    </svg>
  );
}

function CalendarDetailIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 3v4M16 3v4M3 10h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
